import React from "react";

import ClickAwayListener from "@mui/material/ClickAwayListener";
import Collapse from "@mui/material/Collapse";

import Drawer from "@vapor/react-material/Drawer";
import { useTheme } from "@vapor/react-material/styles";
import Box from "@vapor/react-material/Box";
import IconButton from "@vapor/react-material/IconButton";
import List from "@vapor/react-material/List";
import ListItemText from "@vapor/react-material/ListItemText";
import VaporHeaderBar from "@vapor/react-custom/VaporHeaderBar";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons/faArrowLeft";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { SublevelListItemButton } from "./ListItemButton";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";

export const SubLevelMenu = ({
  openPopover,
  drawerOpen,
  handlePopoverClose,
  showUpperLevel,
  sublevel,
  setSublevel,
  setSublevelSelectedIndex,
  sublevelSelectedIndex,
  slideRef,
  focusOn
}) => {
  const theme: any = useTheme();

  const listItemRefs = React.useRef([]);

  const listItemRef = (childIndex) => (el) => {
    listItemRefs.current[childIndex] = el;
  };

  const moveFocusUp = (childIndex) => {
    const upChild = listItemRefs.current[childIndex - 1];
    upChild?.focus();
  };

  const moveFocusDown = (childIndex) => {
    const upChild = listItemRefs.current[childIndex + 1];
    upChild?.focus();
  };

  const openSubLevel = ({ childIndex }) => {
    listItemRefs.current[childIndex]?.click();
  };

  const handleKeyDown =
    ({ childIndex }) =>
    (e) => {
      if (e.key === "ArrowUp") {
        moveFocusUp(childIndex);
        return;
      }

      if (e.key === "ArrowDown") {
        moveFocusDown(childIndex);
        return;
      }

      if (e.key === "ArrowRight") {
        openSubLevel({ childIndex });
        return;
      }

      if (e.key === "ArrowLeft") {
        showUpperLevel(sublevel);
        if (!sublevel.parent?.parent) {
          focusOn(sublevel.ancestor);
        }
        return;
      }

      if (e.key === "Escape") {
        handlePopoverClose();
        return;
      }
    };

  return (
    <ClickAwayListener
      mouseEvent={openPopover && !drawerOpen ? "onMouseDown" : false}
      onClickAway={() => {
        handlePopoverClose();
      }}
    >
      <Drawer
        open={openPopover}
        onClose={handlePopoverClose}
        variant="persistent"
        PaperProps={{
          variant: "menuItem"
        }}
        ModalProps={{
          keepMounted: true
        }}
        SlideProps={{
          direction: drawerOpen ? "left" : "right",
          container: slideRef,
          timeout: 0
        }}
        sx={{
          marginLeft: "auto",
          "&.MuiDrawer-root": {
            zIndex: drawerOpen
              ? theme.zIndex.drawer + 10
              : theme.zIndex.drawer - 10,
            overflowX: "hidden"
          },
          "&.MuiDrawer-root .MuiPaper-root.MuiDrawer-paperAnchorLeft": {
            top: "48px !important",
            left: "48px !important",
            width: "auto",
            height: "100%",
            background: theme.palette.primary.surfaceDefaultColor,
            boxShadow: "0px 4px 16px rgba(21, 41, 53, 0.25)",
            borderRight: 0
          }
        }}
        className={`${openPopover ? "open" : ""}`}
      >
        <Collapse
          in={openPopover}
          orientation="horizontal"
          timeout={300}
          sx={{
            overflow: "hidden",
            transformOrigin: drawerOpen && "top right",
            transition: drawerOpen && `transform ${300}ms`,
            transform: drawerOpen && "translateX(100%)", // Initial state, fully off-screen to the right

            "&.open": {
              transform: drawerOpen && "translateX(0)" // Open state, fully visible
            }
          }}
          className={`${openPopover ? "open" : ""}`}
        >
          <Box
            sx={{
              width: 200,
              "&.MuiBox-root .MuiTypography-root.MuiTypography-titleSmall.MuiTypography-root":
                {
                  display: "-webkit-box",
                  textOverflow: "ellipsis",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.1
                }
            }}
          >
            <VaporHeaderBar
              reduced
              size="small"
              title={sublevel?.label}
              divider
              leftItems={[
                sublevel?.parent.parent !== undefined && (
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => showUpperLevel(sublevel)}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </IconButton>
                )
              ]}
              rightItems={[
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => handlePopoverClose()}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </IconButton>
              ]}
            />
            <List disablePadding role="menu">
              {sublevel?.children &&
                sublevel.children.map((child: any, childIndex) => (
                  <SublevelListItemButton
                    ref={listItemRef(childIndex)}
                    key={child?.label + childIndex}
                    onClick={() => {
                      if (child.children) {
                        setSublevel(child);
                        setSublevelSelectedIndex(null);
                      } else setSublevelSelectedIndex(childIndex);
                      child.onClickFunction && child.onClickFunction();
                      child.closePopoverAfterClick && handlePopoverClose();
                    }}
                    onKeyDown={handleKeyDown({ childIndex })}
                    selected={sublevelSelectedIndex === childIndex}
                    role="menuitem"
                    aria-label={child?.label}
                    autoFocus={childIndex === 0}
                  >
                    <ListItemText
                      primary={child?.label}
                      sx={{
                        my: 0,
                        "&.MuiListItemText-root .MuiTypography-root": {
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis"
                        }
                      }}
                    />
                    {child?.children && (
                      <FontAwesomeIcon
                        style={{
                          color: theme.palette.primary.iconDefault,
                          width: 7,
                          height: 12
                        }}
                        icon={faAngleRight}
                      />
                    )}
                  </SublevelListItemButton>
                ))}
            </List>
          </Box>
        </Collapse>
      </Drawer>
    </ClickAwayListener>
  );
};
